import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  File,
  FileCode2,
  FileImage,
  FileText,
  Film,
  Globe2,
  Link2,
  ListFilter,
  Music2,
  Presentation,
  TriangleAlert,
} from 'lucide-react';
import { useState, type ComponentType } from 'react';

import { useMonkeysLocaleMessages } from '../../provider';
import { cn } from '../layout';
import { Button } from '../primitives';
import type {
  AgentWorkbenchArtifactItem,
  AgentWorkbenchArtifactKind,
} from './artifact-model';

export interface AgentWorkbenchArtifactSummaryProps {
  artifacts: readonly AgentWorkbenchArtifactItem[];
  open?: boolean;
  defaultOpen?: boolean;
  maxItemsPerGroup?: number;
  showAll?: boolean;
  className?: string;
  contentClassName?: string;
  onOpenChange?: (open: boolean) => void;
  onArtifactOpen?: (artifact: AgentWorkbenchArtifactItem) => void;
  onViewAll?: () => void;
}

const ARTIFACT_ICONS: Record<AgentWorkbenchArtifactKind, ComponentType<{ className?: string }>> = {
  audio: Music2,
  file: File,
  html: FileCode2,
  image: FileImage,
  markdown: FileText,
  pdf: FileText,
  presentation: Presentation,
  video: Film,
  web: Globe2,
};

function ArtifactThumbnail({ artifact }: { artifact: AgentWorkbenchArtifactItem }) {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  if (artifact.status === 'loading') {
    return <span className="size-7 shrink-0 animate-pulse rounded bg-muted" aria-hidden="true" />;
  }
  if (artifact.status === 'error') {
    return (
      <span className="flex size-7 shrink-0 items-center justify-center rounded bg-destructive/10 text-destructive-text">
        <TriangleAlert className="size-4" aria-hidden="true" />
      </span>
    );
  }
  if (artifact.thumbnailUrl && !thumbnailFailed) {
    return (
      <img
        src={artifact.thumbnailUrl}
        alt=""
        loading="lazy"
        className="size-7 shrink-0 rounded object-cover"
        onError={() => setThumbnailFailed(true)}
      />
    );
  }
  const Icon = ARTIFACT_ICONS[artifact.kind];
  return (
    <span className="flex size-7 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
      <Icon className="size-4" aria-hidden="true" />
    </span>
  );
}

function ArtifactGroup({
  title,
  artifacts,
  maxItems,
  onArtifactOpen,
}: {
  title: string;
  artifacts: readonly AgentWorkbenchArtifactItem[];
  maxItems: number;
  onArtifactOpen?: (artifact: AgentWorkbenchArtifactItem) => void;
}) {
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <div role="heading" aria-level={2} className="text-sm font-medium text-muted-foreground">{title}</div>
      {artifacts.slice(0, maxItems).map((artifact) => (
        <Button
          key={artifact.id}
          type="button"
          variant="ghost"
          className="h-auto min-w-0 justify-start gap-2 px-0.5 py-0.5 text-left text-sm font-normal disabled:cursor-default"
          disabled={!onArtifactOpen || artifact.status === 'loading'}
          onClick={() => onArtifactOpen?.(artifact)}
        >
          <ArtifactThumbnail artifact={artifact} />
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{artifact.name}</span>
        </Button>
      ))}
    </section>
  );
}

export function AgentWorkbenchArtifactSummary({
  artifacts,
  open,
  defaultOpen = false,
  maxItemsPerGroup = 3,
  showAll = false,
  className,
  contentClassName,
  onOpenChange,
  onArtifactOpen,
  onViewAll,
}: AgentWorkbenchArtifactSummaryProps) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.artifactWorkspace;
  const outputs = artifacts.filter((artifact) => artifact.origin === 'output');
  const sources = artifacts.filter((artifact) => artifact.origin === 'source');
  if (artifacts.length === 0) return null;

  return (
    <PopoverPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger
        type="button"
        className={cn(
          'flex size-9 items-center justify-center rounded-xl bg-muted text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:text-foreground',
          className,
        )}
        aria-label={messages.toggleSummary}
      >
        <ListFilter className="size-4" aria-hidden="true" />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Content
        aria-label={messages.toggleSummary}
        side="bottom"
        align="end"
        sideOffset={8}
        collisionPadding={12}
        className={cn(
          'z-50 w-[min(22rem,calc(100vw-1.5rem))] rounded-3xl border border-border bg-popover p-4 text-popover-foreground shadow-xl outline-none',
          contentClassName,
        )}
      >
        <div className="flex max-h-[min(34rem,calc(100vh-5rem))] min-w-0 flex-col gap-4 overflow-y-auto">
          {outputs.length > 0 ? (
            <ArtifactGroup
              title={messages.outputs}
              artifacts={outputs}
              maxItems={showAll ? outputs.length : maxItemsPerGroup}
              onArtifactOpen={onArtifactOpen}
            />
          ) : null}
          {outputs.length > 0 && sources.length > 0 ? <div className="h-px bg-border" /> : null}
          {sources.length > 0 ? (
            <ArtifactGroup
              title={messages.sources}
              artifacts={sources}
              maxItems={showAll ? sources.length : maxItemsPerGroup}
              onArtifactOpen={onArtifactOpen}
            />
          ) : null}
          {onViewAll && !showAll && (outputs.length > maxItemsPerGroup || sources.length > maxItemsPerGroup) ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-8 gap-2 px-0 text-sm text-muted-foreground hover:text-foreground"
              onClick={onViewAll}
            >
              <Link2 className="size-4" aria-hidden="true" />
              <span>{messages.viewAll}</span>
            </Button>
          ) : null}
        </div>
        <PopoverPrimitive.Arrow className="fill-popover" />
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Root>
  );
}
